import { APP_BASE } from '../app/base';

// Thin fetch wrapper for the PHP API. Every endpoint returns JSON; errors are { error: string }.
// Every mutating request carries the X-VDS header, which is the CSRF token (see Auth.php).

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/** True for failures where the server was never reached (offline, DNS, aborted). */
export class NetworkError extends Error {}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export interface RequestOptions {
  /** Let the request finish after the page is gone (body must stay under 64 KB). */
  keepalive?: boolean;
}

async function request<T>(method: string, path: string, body?: unknown, form?: FormData, opts?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-VDS': '1' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${APP_BASE}api${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
      keepalive: opts?.keepalive,
    });
  } catch (e) {
    throw new NetworkError(e instanceof Error ? e.message : 'Network error');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, `Non-JSON response (${res.status})`);
  }
  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `HTTP ${res.status}`;
    if (res.status === 401 && !path.startsWith('/auth/')) onUnauthorized?.();
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body ?? {}, undefined, opts),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, undefined, form),
};

// ---- binary helpers for Yjs updates over JSON -------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
  }
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
